/**
 * Background work that must never cost a frame: the Library's thumbnails (decrypt, decode, resize, cache, the
 * module's texture uploads) run here in small turns, at most FRAME_WORK_BUDGET_MS of them after each animation
 * frame, and none after a frame that ran long (the page is busy: it waits for a smooth one, a few frames at most).
 *
 * A turn is one short synchronous step. Heavy work never runs in a turn itself: it goes to the browser's own
 * threads (WebCrypto, createImageBitmap, OffscreenCanvas encoding) between turns, and only its glue comes back
 * here. Every caller shares the one budget, so however many thumbnails are in flight the page's thread gives them
 * the same few milliseconds a frame.
 *
 *   await frameWork.turn();          // the continuation runs inside this frame's slice
 *   const url = await frameWork.run(() => URL.createObjectURL(blob));
 */

/** Milliseconds of background work allowed after each animation frame. */
export const FRAME_WORK_BUDGET_MS = 4;
/** A frame that took longer than this (ms) gets no background work after it... */
export const FRAME_WORK_BUSY_FRAME_MS = 24;
/** ...unless this many frames in a row were skipped: a page that is always that slow still gets its slices. */
export const FRAME_WORK_MAX_SKIPPED_FRAMES = 3;
/** Without animation frames (a hidden tab), turns still come this often (ms), one at a time. */
const IDLE_TURN_MS = 50;

type Waiter = () => void;

class FrameWorkScheduler {
  private readonly waiting: Waiter[] = [];
  private sliceStart = -1;
  private lastFrame = -1;
  private skipped = 0;
  private frameRequested = false;
  private draining = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly channel: MessageChannel | null =
    typeof MessageChannel !== 'undefined' ? new MessageChannel() : null;

  constructor() {
    if (this.channel) this.channel.port1.onmessage = () => this.drain();
  }

  /** Resolves when this frame's slice has room; the awaiting code then runs inside it. */
  turn(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.waiting.push(resolve);
      this.schedule();
    });
  }

  /** `step` (one short synchronous step) run in a turn; its result, or its throw as a rejection. */
  async run<T>(step: () => T): Promise<T> {
    await this.turn();
    return step();
  }

  /** How many steps are waiting (for tests and diagnostics). */
  pending(): number {
    return this.waiting.length;
  }

  private schedule(): void {
    if (this.frameRequested || this.draining) return;
    if (typeof requestAnimationFrame !== 'function') {
      this.later();
      return;
    }
    this.frameRequested = true;
    requestAnimationFrame((now) => this.onFrame(now));
    // A hidden page gets no frames: a slow trickle keeps the work going.
    this.later();
  }

  private later(): void {
    if (this.idleTimer !== null) return;
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null;
      if (this.waiting.length === 0) return;
      // A visible page's frames drive the turns; this is for a hidden one.
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
      this.waiting.shift()?.();
      if (this.waiting.length > 0) this.later();
    }, IDLE_TURN_MS);
  }

  private onFrame(now: number): void {
    this.frameRequested = false;
    const frameTime = this.lastFrame < 0 ? 0 : now - this.lastFrame;
    this.lastFrame = now;
    if (this.waiting.length === 0) {
      this.lastFrame = -1;  // idle: the next frame's interval says nothing about load
      return;
    }
    if (frameTime > FRAME_WORK_BUSY_FRAME_MS && this.skipped < FRAME_WORK_MAX_SKIPPED_FRAMES) {
      this.skipped += 1;
      this.schedule();  // the page is busy: wait for a smooth frame (a few at most)
      return;
    }
    this.skipped = 0;
    // After the frame (its rendering is done): a task of its own, then turns while the slice lasts.
    this.sliceStart = performance.now();
    this.draining = true;
    if (this.channel) this.channel.port2.postMessage(0);
    else setTimeout(() => this.drain(), 0);
  }

  private drain(): void {
    if (this.waiting.length === 0) {
      this.draining = false;
      this.lastFrame = -1;
      return;
    }
    if (performance.now() - this.sliceStart >= FRAME_WORK_BUDGET_MS) {
      this.draining = false;
      this.schedule();
      return;
    }
    // One turn per task: its continuation (microtasks) runs before the next message, so it counts in the slice.
    this.waiting.shift()?.();
    if (this.channel) this.channel.port2.postMessage(0);
    else setTimeout(() => this.drain(), 0);
  }
}

/** The page's one background-work scheduler. */
export const frameWork = new FrameWorkScheduler();
